import {
  PRODUCT_CHASSIS_SERIES_STATUS,
  STATUS_COMMON,
} from '../../../utils/enum';
import ApiService from '../api.service';

const productService = {
  /**
   * Search products
   * @param {Object} params - Search parameters
   * @param {string} params.storeId - Store ID
   * @param {string} params.searchProduct - Search text
   * @returns {Promise}
   */
  searchProducts(params) {
    return ApiService.query(`products/search`, {
      params,
    });
  },

  /**
   * Get product list
   * @param {Object} params - List parameters
   * @returns {Promise}
   */
  getProducts(params) {
    return ApiService.query(`products`, {
      params,
    });
  },

  /**
   * Get product detail
   * @param {string} id - Product ID
   * @returns {Promise}
   */
  getProductDetail(id) {
    return ApiService.query(`products/${id}`);
  },

  /**
   * Create product
   * @param {Object} data - Product data
   * @returns {Promise}
   */
  createProduct(data) {
    return ApiService.post('products', data);
  },

  /**
   * Update product
   * @param {Object} data - Product data
   * @returns {Promise}
   */
  updateProduct(data) {
    return ApiService.put('products', data);
  },

  /**
   * Delete product
   * @param {string} id - Product ID
   * @returns {Promise}
   */
  deleteProduct(id) {
    return ApiService.delete(`products/${id}`);
  },

  /**
   * Get product stock
   * @param {Object} params - Stock parameters
   * @returns {Promise}
   */
  getProductStock(params) {
    const queryParams = new URLSearchParams(params).toString();
    return ApiService.query(`products/stock?${queryParams}`);
  },

  /**
   * Update product price
   * @param {Object} data - Price data
   * @returns {Promise}
   */
  updatePrice(data) {
    return ApiService.put('products/update-price', data);
  },

  /**
   * Get product types
   * @returns {Promise}
   */
  getProductTypes() {
    return ApiService.query('products/type');
  },
  /**
   * Search products by chassis number
   * @param {Object} params - Search parameters
   * @returns {Promise}
   */
  searchProductsByChassisNumber(params) {
    const queryParams = new URLSearchParams(params).toString();
    return ApiService.query(`products/search/chassis-number?${queryParams}`);
  },

  /**
   * Search products by serial number
   * @param {Object} params - Search parameters
   * @returns {Promise}
   */
  searchProductsBySerialNumber(params) {
    const queryParams = new URLSearchParams(params).toString();
    return ApiService.query(`products/search/serial-number?${queryParams}`);
  },

  /**
   * Get products by chassis number
   * @param {string} storeId - Store ID
   * @param {string} chassisNumber - Chassis number
   * @returns {Promise}
   */
  getProductsByChassisNumber(storeId, chassisNumber) {
    return ApiService.query(
      `products/${storeId}/chassis-product/${chassisNumber}`,
    );
  },

  /**
   * Export products to Excel
   * @param {Object} params - Export parameters
   * @returns {Promise}
   */
  exportExcel(params) {
    const queryParams = new URLSearchParams(params).toString();
    return ApiService.query(`products/export-excel?${queryParams}`, {
      responseType: 'blob',
    });
  },

  /**
   * Preview import Excel
   * @param {File} file - Excel file
   * @returns {Promise}
   */
  previewImportExcel(file) {
    const formData = new FormData();
    formData.append('file', file);
    return ApiService.post('products/import-preview', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  /**
   * Import Excel
   * @param {Object} data - Import data
   * @returns {Promise}
   */
  importExcel(data) {
    return ApiService.post('products/import', data);
  },

  async getEngineNumberByChassisNumber(
    chassisNumber,
    sourceStoreId = '',
    isExportMode = false,
    productId = '',
  ) {
    try {
      // productId giới hạn về đúng dòng sản phẩm đang xuất: một số khung có
      // thể có nhiều row (đã trả NCC rồi nhập lại với sản phẩm/số máy mới).
      const query = productId
        ? `products/${sourceStoreId}/chassis-product/${chassisNumber}?productId=${productId}`
        : `products/${sourceStoreId}/chassis-product/${chassisNumber}`;
      const { data } = await ApiService.query(query);

      if (data.status !== STATUS_COMMON.ACTIVE) return '';

      const rows = (data.data || []).filter((p) => p?.engineNumber);
      if (rows.length === 0) return '';

      if (isExportMode) {
        // Chỉ xuất được đơn vị đang còn trong kho (NEW); bỏ qua row lịch sử
        // (RETURN_TO_SUPPLIER, SALED, ...). Một số khung chỉ có 1 row NEW.
        const exportable = rows.filter(
          (p) => p.status === PRODUCT_CHASSIS_SERIES_STATUS.NEW,
        );
        return exportable.length === 1 ? exportable[0].engineNumber : '';
      }

      return rows.length === 1 ? rows[0].engineNumber : '';
    } catch (error) {
      console.error('Error fetching engine number:', error);
      return '';
    }
  },

  updateEngineNumber(payload) {
    return ApiService.patch('products/update-engine-number', payload);
  },
};

export default productService;
